/**
 * CreateCoinBalanceChip – arcade-token coin bar, opens the coin store on press.
 * On web (or when EXPO_PUBLIC_COIN_STORE_MOBILE_ENABLED=true): opens the coin store.
 * On mobile without the flag: opens daily rewards (same as the menu header coin tap).
 */

import React, { useMemo, useState, useCallback } from 'react';
import { Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../../../src/theme/useTheme';
import { useGuestGuard } from '../../../src/shared/hooks/useGuestGuard';
import { GUEST_AUTH_ACTIONS } from '../../../src/shared/hooks/useGuestGuard.constants';
import { CoinStoreModal } from '../../../src/economy/components/CoinStoreModal';
import { DailyRewardModal } from '../../../src/economy/components/DailyRewardModal';
import { useEconomyStore } from '../../../src/shared/stores/economyStore';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { getCreateCoinBalanceChipStyles } from './CreateCoinBalanceChip.styles';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';

const COIN_STORE_MOBILE_ENABLED = process.env.EXPO_PUBLIC_COIN_STORE_MOBILE_ENABLED === 'true';
const showCoinStore = Platform.OS === 'web' || COIN_STORE_MOBILE_ENABLED;

interface CreateCoinBalanceChipProps {
  coins: number;
  /** When false, the balance has not been fetched yet — show a placeholder, not a misleading 0. */
  loaded?: boolean;
}

export const CreateCoinBalanceChip: React.FC<CreateCoinBalanceChipProps> = ({ coins, loaded = true }) => {
  const theme = useTheme();
  const styles = useMemo(() => getCreateCoinBalanceChipStyles(theme), [theme]);
  const [modalVisible, setModalVisible] = useState(false);
  const { requireAuth } = useGuestGuard();
  const claimDaily = useEconomyStore((s) => s.claimDaily);
  const apiClient = useApiClient();

  const handlePress = useCallback(() => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    setModalVisible(true);
  }, [requireAuth]);

  const handleClaim = useCallback(async () => {
    await claimDaily(apiClient as (endpoint: string, options?: Record<string, unknown>) => Promise<{ status: number; data: unknown }>);
  }, [claimDaily, apiClient]);

  return (
    <>
      <Pressable
        style={({ pressed, hovered }) => [
          styles.chip,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && styles.chipPressed,
          hovered && Platform.OS === 'web' && styles.chipHover,
        ]}
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={showCoinStore ? 'Open coin store' : 'Collect daily coins'}
      >
        <Image
          source={IDLE_COIN_SOURCE}
          style={styles.coinIcon}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
        <Text style={styles.value} numberOfLines={1}>
          {loaded ? new Intl.NumberFormat().format(coins) : '—'}
        </Text>
      </Pressable>

      {showCoinStore ? (
        <CoinStoreModal
          isVisible={modalVisible}
          onDismiss={() => setModalVisible(false)}
        />
      ) : (
        <DailyRewardModal
          isVisible={modalVisible}
          onDismiss={() => setModalVisible(false)}
          onClaim={handleClaim}
        />
      )}
    </>
  );
};
